/**
 * HTML Template for TestRelic Analytics Report
 *
 * Orchestrator: imports CSS, logo, and JS modules; assembles them
 * into a single self-contained HTML document.
 */

import { CSS } from './html-css.js';
import { LOGO_SVG, LOGO_SVG_RAW } from './html-logo.js';
import { JS_RENDER } from './html-js-render.js';
import { JS_NETWORK } from './html-js-network.js';
import { JS_FILTERS } from './html-js-filters.js';
import { JS_INTERACTIONS } from './html-js-interactions.js';
import { JS_ACTIONS } from './html-js-actions.js';
import { JS_CONSOLE } from './html-js-console.js';
import { JS_ARTIFACTS } from './html-js-artifacts.js';

const JS = `
(function(){
  var data=JSON.parse(document.getElementById('report-data').textContent);
  ${JS_RENDER}
  ${JS_ACTIONS}
  ${JS_CONSOLE}
  ${JS_NETWORK}
  ${JS_FILTERS}
  ${JS_ARTIFACTS}
  ${JS_INTERACTIONS}
  applyTheme();
  renderRunMeta();
  renderSummary();
  initFilters();
  renderFilterBar();
  renderTestGrid();
  var _lo=document.getElementById('loading-overlay');if(_lo)_lo.remove();
})();`;

export function renderHtmlDocument(reportJson: string, serverPort?: number | null, manifestJson?: string | null): string {
  // Escape </ sequences to prevent HTML parser from closing <script> tags prematurely.
  // JSON spec allows \/ as an escape for /, so <\/ is transparent to JSON.parse.
  const safeJson = reportJson.replace(/<\//g, '<\\/');
  const safeManifestJson = manifestJson ? manifestJson.replace(/<\//g, '<\\/') : null;
  return `<!-- TestRelic AI Analytics Report — self-contained, no external dependencies -->
<!DOCTYPE html>
<html lang="en" data-theme="">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; script-src 'unsafe-inline'; img-src data: blob: 'self'; media-src blob: 'self'; connect-src 'self' http://127.0.0.1:*;">
<title>TestRelic AI Analytics Report</title>
${serverPort ? `<meta name="artifact-server-port" content="${serverPort}">` : ''}
<link rel="icon" type="image/svg+xml" href="data:image/svg+xml;base64,${Buffer.from(LOGO_SVG_RAW).toString('base64')}">
<style>${CSS}</style>
<script>(function(){var p=localStorage.getItem('tr-theme')||'system';var t=p==='system'?(window.matchMedia('(prefers-color-scheme:light)').matches?'light':'dark'):p;document.documentElement.setAttribute('data-theme',t);})()</script>
</head>
<body>
<div class="wrap">
  <div class="top-bar">
    <div class="brand">
      ${LOGO_SVG}
      <div class="brand-text">
        <span class="brand-title">TestRelic AI</span>
        <span class="brand-sub">Analytics Report</span>
      </div>
    </div>
    <div class="run-meta" id="run-meta"></div>
    <div class="theme-toggle" id="theme-toggle">
      <button class="theme-btn" data-theme-val="system" title="System"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg></button>
      <button class="theme-btn" data-theme-val="light" title="Light"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg></button>
      <button class="theme-btn" data-theme-val="dark" title="Dark"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg></button>
    </div>
    <a class="cta-btn" href="https://testrelic.ai" target="_blank" rel="noopener noreferrer"><svg class="cta-icon" viewBox="0 0 16 16" fill="none"><path d="M8 1l1.545 4.955L14.5 7.5l-4.955 1.545L8 14l-1.545-4.955L1.5 7.5l4.955-1.545L8 1z" fill="currentColor"/></svg>Advanced Insights with AI<span class="cta-sep">–</span><strong>Get Started</strong><svg class="cta-arrow" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M2.5 6h7M6.5 3l3 3-3 3"/></svg></a>
  </div>
  <div id="summary-strip" class="summary-strip"></div>
  <div id="filter-bar" class="filter-bar"></div>
  <div id="test-grid"></div>
</div>
<div class="loading-overlay" id="loading-overlay"><div class="loading-spinner"></div><div class="loading-text">Loading report...</div></div>
<div class="drawer-backdrop" id="drawer-backdrop"></div>
<aside class="drawer" id="drawer">
  <div class="drawer-bar">
    <span class="drawer-bar-title">Test Details</span>
    <button class="drawer-close" id="drawer-close">&times;</button>
  </div>
  <div class="drawer-body" id="drawer-body"></div>
</aside>
<div class="filter-drawer-backdrop" id="filter-drawer-backdrop"></div>
<aside class="filter-drawer" id="filter-drawer">
  <div class="filter-drawer-bar">
    <span class="filter-drawer-title">Filters</span>
    <button class="filter-drawer-close" id="filter-drawer-close">&times;</button>
  </div>
  <div class="filter-drawer-body" id="filter-drawer-body"></div>
</aside>
<script id="report-data" type="application/json">${safeJson}</script>
${safeManifestJson ? `<script id="artifact-manifest-data" type="application/json">${safeManifestJson}</script>` : ''}
<script>${JS}</script>
</body>
</html>`;
}

/**
 * Render a streaming-mode HTML document.
 * Embeds only the summary and compact test index — detail data is fetched from the server.
 */
export function renderStreamingHtmlDocument(
  summaryJson: string,
  indexJson: string,
  serverPort?: number | null,
  manifestJson?: string | null,
): string {
  const safeSummary = summaryJson.replace(/<\//g, '<\\/');
  // Use indexJson directly — caller provides pre-compacted index (from index-compact.json)
  // to avoid expensive parse-reserialize of the full index for large suites.
  const safeIndex = indexJson.replace(/<\//g, '<\\/');
  const safeManifest = manifestJson ? manifestJson.replace(/<\//g, '<\\/') : null;

  // Build a minimal report-data object that the existing JS modules can consume.
  // The timeline is empty — details are served on-demand from the server.
  // Parse summary to extract top-level fields for the report header
  let summaryObj: Record<string, unknown> = {};
  try { summaryObj = JSON.parse(summaryJson); } catch { /* use defaults */ }
  const streamingData = `{
    "schemaVersion":"2.0",
    "reportMode":"streaming",
    "serverPort":${serverPort ?? 'null'},
    "summary":${safeSummary},
    "index":${safeIndex},
    "timeline":[],
    "testRunId":${JSON.stringify(String(summaryObj.testRunId ?? ''))},
    "startedAt":${JSON.stringify(String(summaryObj.startedAt ?? ''))},
    "completedAt":${JSON.stringify(String(summaryObj.completedAt ?? ''))},
    "totalDuration":${Number(summaryObj.totalDuration) || 0},
    "ci":${summaryObj.ci ? JSON.stringify(summaryObj.ci).replace(/<\//g, '<\\/') : 'null'},
    "metadata":${summaryObj.metadata ? JSON.stringify(summaryObj.metadata).replace(/<\//g, '<\\/') : 'null'},
    "shardRunIds":${summaryObj.shardRunIds ? JSON.stringify(summaryObj.shardRunIds).replace(/<\//g, '<\\/') : 'null'}
  }`;

  const JS_STREAMING = `
(function(){
  var data=JSON.parse(document.getElementById('report-data').textContent);
  var _serverPort=data.serverPort||null;
  var _serverAvailable=false;
  var _detailCache={};
  var _detailCacheKeysS=[];
  var _detailCacheMaxS=50;
  var _testIndex=data.index||[];

  /* Resolve the API base URL: if served from http, use same origin; otherwise use explicit port */
  function getApiBase(){
    if(window.location.protocol==='http:'||window.location.protocol==='https:'){
      return window.location.origin;
    }
    if(_serverPort){return 'http://127.0.0.1:'+_serverPort;}
    return null;
  }
  var _apiBase=getApiBase();

  /* Check server availability */
  function checkServer(cb){
    if(!_apiBase){cb(false);return;}
    fetch(_apiBase+'/api/health',{method:'GET'}).then(function(r){
      _serverAvailable=r.ok;cb(r.ok);
    }).catch(function(){_serverAvailable=false;cb(false);});
  }

  /* Bounded detail cache (LRU, max 50 entries) */
  function sCacheDetail(testId,d){
    if(_detailCache[testId]){
      var ki=_detailCacheKeysS.indexOf(testId);
      if(ki>-1)_detailCacheKeysS.splice(ki,1);
    }else if(_detailCacheKeysS.length>=_detailCacheMaxS){
      var evict=_detailCacheKeysS.shift();
      delete _detailCache[evict];
    }
    _detailCache[testId]=d;
    _detailCacheKeysS.push(testId);
  }

  /* Fetch test detail metadata from server */
  function fetchTestDetail(testId,cb){
    if(_detailCache[testId]){cb(null,_detailCache[testId]);return;}
    if(!_serverAvailable||!_apiBase){cb('Server unavailable');return;}
    fetch(_apiBase+'/api/tests/'+testId).then(function(r){
      if(!r.ok)throw new Error('HTTP '+r.status);
      return r.json();
    }).then(function(d){
      sCacheDetail(testId,d);cb(null,d);
    }).catch(function(e){cb(e.message||'Failed to fetch');});
  }

  /* Fetch paginated heavy data (network, console, api-calls) */
  function fetchTestData(testId,dataType,page,pageSize,cb){
    if(!_serverAvailable||!_apiBase){cb('Server unavailable');return;}
    var url=_apiBase+'/api/tests/'+testId+'/'+dataType+'?page='+page+'&pageSize='+pageSize;
    fetch(url).then(function(r){
      if(!r.ok)throw new Error('HTTP '+r.status);
      return r.json();
    }).then(function(d){cb(null,d);}).catch(function(e){cb(e.message||'Failed to fetch');});
  }
  window.__trFetchTestData=fetchTestData;

  /* Make fetchTestDetail and index available globally for other modules */
  window.__trStreamingFetchDetail=fetchTestDetail;
  window.__trTestIndex=_testIndex;
  window.__trServerPort=_serverPort;
  window.__trIsStreaming=true;

  ${JS_RENDER}
  ${JS_ACTIONS}
  ${JS_CONSOLE}
  ${JS_NETWORK}
  ${JS_FILTERS}
  ${JS_ARTIFACTS}
  ${JS_INTERACTIONS}
  applyTheme();
  renderRunMeta();
  renderSummary();
  initFilters();
  renderFilterBar();
  renderTestGrid();
  checkServer(function(ok){});
  var _lo=document.getElementById('loading-overlay');if(_lo)_lo.remove();
})();`;

  return `<!-- TestRelic AI Analytics Report — streaming mode -->
<!DOCTYPE html>
<html lang="en" data-theme="">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; script-src 'unsafe-inline'; img-src data: blob: 'self'; media-src blob: 'self'; connect-src 'self' http://127.0.0.1:*;">
<title>TestRelic AI Analytics Report</title>
${serverPort ? `<meta name="report-server-port" content="${serverPort}">` : ''}
${serverPort ? `<meta name="artifact-server-port" content="${serverPort}">` : ''}
<link rel="icon" type="image/svg+xml" href="data:image/svg+xml;base64,${Buffer.from(LOGO_SVG_RAW).toString('base64')}">
<style>${CSS}</style>
<script>(function(){var p=localStorage.getItem('tr-theme')||'system';var t=p==='system'?(window.matchMedia('(prefers-color-scheme:light)').matches?'light':'dark'):p;document.documentElement.setAttribute('data-theme',t);})()</script>
</head>
<body>
<div class="wrap">
  <div class="top-bar">
    <div class="brand">
      ${LOGO_SVG}
      <div class="brand-text">
        <span class="brand-title">TestRelic AI</span>
        <span class="brand-sub">Analytics Report</span>
      </div>
    </div>
    <div class="run-meta" id="run-meta"></div>
    <div class="theme-toggle" id="theme-toggle">
      <button class="theme-btn" data-theme-val="system" title="System"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg></button>
      <button class="theme-btn" data-theme-val="light" title="Light"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg></button>
      <button class="theme-btn" data-theme-val="dark" title="Dark"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg></button>
    </div>
    <a class="cta-btn" href="https://testrelic.ai" target="_blank" rel="noopener noreferrer"><svg class="cta-icon" viewBox="0 0 16 16" fill="none"><path d="M8 1l1.545 4.955L14.5 7.5l-4.955 1.545L8 14l-1.545-4.955L1.5 7.5l4.955-1.545L8 1z" fill="currentColor"/></svg>Advanced Insights with AI<span class="cta-sep">–</span><strong>Get Started</strong><svg class="cta-arrow" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M2.5 6h7M6.5 3l3 3-3 3"/></svg></a>
  </div>
  <div id="summary-strip" class="summary-strip"></div>
  <div id="filter-bar" class="filter-bar"></div>
  <div id="test-grid"></div>
</div>
<div class="loading-overlay" id="loading-overlay"><div class="loading-spinner"></div><div class="loading-text">Loading report...</div></div>
<div class="drawer-backdrop" id="drawer-backdrop"></div>
<aside class="drawer" id="drawer">
  <div class="drawer-bar">
    <span class="drawer-bar-title">Test Details</span>
    <button class="drawer-close" id="drawer-close">&times;</button>
  </div>
  <div class="drawer-body" id="drawer-body"></div>
</aside>
<div class="filter-drawer-backdrop" id="filter-drawer-backdrop"></div>
<aside class="filter-drawer" id="filter-drawer">
  <div class="filter-drawer-bar">
    <span class="filter-drawer-title">Filters</span>
    <button class="filter-drawer-close" id="filter-drawer-close">&times;</button>
  </div>
  <div class="filter-drawer-body" id="filter-drawer-body"></div>
</aside>
<script id="report-data" type="application/json">${streamingData}</script>
${safeManifest ? `<script id="artifact-manifest-data" type="application/json">${safeManifest}</script>` : ''}
<script>${JS_STREAMING}</script>
</body>
</html>`;
}
